import { Injectable } from '@angular/core';
import { ChatLoggingService } from './chat-logging.service';
import { ChatCloudLinkService } from './chat-cloudlink.service';
import { GoogleMaps } from '../classes/constants';
@Injectable({
  providedIn: 'root'
})
export class LocationService {
  private isLocationAvailable: boolean = false;
  public isPermissionDenied: boolean = false;
  public failedToGetLocation: boolean = false;
  public invalidException: boolean = false;
  public googleScriptLoaded: boolean = false;

  constructor(
    private chatLoggingService: ChatLoggingService,
    private chatCloudlinkService: ChatCloudLinkService
  ) {
    if ('geolocation' in navigator) {
      /* geolocation is available */
      this.isLocationAvailable = true;
    } else {
      /* geolocation IS NOT available */
      this.chatLoggingService.info({
        logMessage: 'location.service.ctor() location is not available'
      });
    }

    if (!this.chatCloudlinkService.isOffline()) {
      this.addGoogleMapsScript();
    } else {
      this.chatLoggingService.info({
        logMessage: 'location.service.ctor() Offline, waiting for online'
      });
      let onlineSub = this.chatCloudlinkService.online$.subscribe(online => {
        if (online) {
          onlineSub.unsubscribe();
          this.addGoogleMapsScript();
        }
      });
    }
  }

  addGoogleMapsScript() {
    //Add google API script to document
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.defer = true;
    script.id = 'angular-google-maps-script';
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GoogleMaps.API_KEY}&libraries=marker&loading=async`;
    document.body.appendChild(script);
    this.googleScriptLoaded = true;
  }

  getCurrentPosition(): Promise<GeolocationPosition> {
    return new Promise((resolve, reject) => {
      if (!this.isLocationAvailable) {
        this.chatLoggingService.error({
          logMessage: 'location.service.getCurrentPosition() - no location service',
          logParameters: [this.isLocationAvailable]
        });
        return reject('No location service is available.');
      }

      this.chatLoggingService.info({ logMessage: 'location.service.getCurrentPosition()' });

      const options = { timeout: 5000 };
      let timeoutId: ReturnType<typeof setTimeout>;

      // Hard timeout to prevent hanging
      timeoutId = setTimeout(() => {
        this.failedToGetLocation = true;
        this.chatLoggingService.error({
          logMessage: 'location.service.getCurrentPosition() - Geolocation request timed out (enforced)'
        });
        reject('Geolocation request timed out (enforced).');
      }, 5100);

      navigator.geolocation.getCurrentPosition(
        position => {
          clearTimeout(timeoutId); // Stop the enforced timeout if location is received
          this.chatLoggingService.info({
            logMessage: 'location.service.getCurrentPosition() - Position',
            logParameters: [position]
          });
          this.isPermissionDenied = false;
          this.failedToGetLocation = false;
          this.invalidException = false;
          resolve(position);
        },
        (ex: GeolocationPositionError) => {
          clearTimeout(timeoutId); // Stop the enforced timeout on error
          let errorMessage = this.handleGeolocationError(ex);
          reject(errorMessage);
        },
        options
      );
    });
  }

  private handleGeolocationError(ex: GeolocationPositionError): string {
    let errorMessage = '';
    if (ex) {
      switch (ex.code) {
        case ex.PERMISSION_DENIED:
          this.isPermissionDenied = true;
          errorMessage = 'User denied the request for Geolocation.';
          break;
        case ex.POSITION_UNAVAILABLE:
          this.failedToGetLocation = true;
          errorMessage = 'Location information is unavailable.';
          break;
        case ex.TIMEOUT:
          this.failedToGetLocation = true;
          errorMessage = 'The request to get user location timed out.';
          break;
        default:
          this.failedToGetLocation = true;
          errorMessage = 'An unknown error occurred.';
          break;
      }
      this.chatLoggingService.error({
        logMessage: 'location.service.getCurrentPosition()',
        logParameters: [errorMessage]
      });
    } else {
      this.isPermissionDenied = false;
      this.failedToGetLocation = false;
      this.invalidException = true;
      this.chatLoggingService.error({
        logMessage: 'location.service.getCurrentPosition() - undefined exception',
        logParameters: [ex]
      });
    }
    return errorMessage || ex.message;
  }
}
